import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarBlank, Phone, EnvelopeSimple, ArrowRight, CheckCircle, Heart } from "@phosphor-icons/react";

const DAY_OPTIONS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "anyday", label: "Any weekday" },
];
const TIME_OPTIONS = [
  { value: "morning", label: "Morning (8am – 12pm)" },
  { value: "midday", label: "Midday (12pm – 2pm)" },
  { value: "afternoon", label: "Afternoon (2pm – 5pm)" },
  { value: "evening", label: "Evening (after 5pm)" },
];

export default function BookConsult() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    dog_name: "", dog_age: "",
    preferred_day: "", preferred_time: "",
    suburb: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.preferred_day) {
      toast.error("Please fill in your name, email and preferred day.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        suburb: form.suburb.trim(),
        dog_age: form.dog_age.trim(),
        source: "book-consult-form",
        enquiry: [
          `Free consult request via website.`,
          `Preferred day: ${form.preferred_day}`,
          form.preferred_time ? `Preferred time: ${form.preferred_time}` : null,
          form.dog_name ? `Dog: ${form.dog_name}` : null,
          form.message ? `\nNotes from client:\n${form.message}` : null,
        ].filter(Boolean).join("\n"),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again or email support@puppl.app.");
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Seo title="Free Consult Request Received | Puppl" description="Thanks — the team will be in touch within 24 hours." />
        <PublicHeader />
        <section className="max-w-2xl mx-auto px-6 lg:px-12 py-24 text-center" data-testid="book-consult-success">
          <CheckCircle size={72} weight="duotone" className="text-emerald-500 mx-auto mb-6" />
          <h1 className="font-heading text-4xl lg:text-5xl font-bold text-stone-900 mb-4">You're locked in.</h1>
          <p className="text-lg text-stone-700 leading-relaxed mb-8">
            Thanks {form.name.split(" ")[0]}! the team will be in touch within 24 hours (usually faster) to confirm a time on <strong>{form.preferred_day}</strong>{form.preferred_time ? <> in the <strong>{form.preferred_time}</strong></> : null}.
          </p>
          <p className="text-stone-600 mb-8">
            While you wait, have a browse of our services or our online video library.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/services" className="btn-primary inline-flex items-center gap-2" data-testid="success-services">
              See Services <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/tips" className="btn-secondary inline-flex items-center gap-2" data-testid="success-tips">
              Read training tips
            </Link>
          </div>
        </section>
        <PublicFooter />
        <ChatWidget />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Book a Free Consult | Puppl Sunshine Coast"
        description="Free 15-minute consultation with the Puppl team — Sunshine Coast puppy trainer. Pick your preferred day and the team will give you a call."
        canonical={typeof window !== "undefined" ? window.location.origin + "/book-consult" : ""}
      />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-medium mb-4">
            <Heart size={16} weight="fill" className="text-[#E2B054]" /> Free 15-min consult
          </div>
          <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-3">Book a free consult.</h1>
          <p className="text-lg lg:text-xl opacity-90 max-w-2xl mx-auto">
            Pick a day and time that suits you, tell us a little about your pup, and the team will give you a call to chat through your goals. No charge, no obligation, no spam.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-12">
        <form onSubmit={submit} className="card-pp p-6 lg:p-8 space-y-5" data-testid="book-consult-form">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Your name *" testid="consult-name">
              <input type="text" value={form.name} onChange={(e) => update("name", e.target.value)} required className="form-input" />
            </Field>
            <Field label="Email *" testid="consult-email">
              <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="form-input" />
            </Field>
            <Field label="Mobile" testid="consult-phone">
              <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="form-input" placeholder="04…" />
            </Field>
            <Field label="Suburb" testid="consult-suburb">
              <input type="text" value={form.suburb} onChange={(e) => update("suburb", e.target.value)} className="form-input" placeholder="e.g. Mudjimba" />
            </Field>
          </div>

          <div className="border-t border-stone-100 pt-5">
            <p className="font-heading font-semibold text-stone-900 mb-3 flex items-center gap-2">
              <CalendarBlank size={20} weight="duotone" className="text-[#f38838]" /> When suits you?
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Preferred day *" testid="consult-day">
                <select value={form.preferred_day} onChange={(e) => update("preferred_day", e.target.value)} required className="form-input">
                  <option value="">-- Pick a day --</option>
                  {DAY_OPTIONS.map((d) => <option key={d.value} value={d.label}>{d.label}</option>)}
                </select>
              </Field>
              <Field label="Time of day" testid="consult-time">
                <select value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)} className="form-input">
                  <option value="">No preference</option>
                  {TIME_OPTIONS.map((t) => <option key={t.value} value={t.label}>{t.label}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="border-t border-stone-100 pt-5">
            <p className="font-heading font-semibold text-stone-900 mb-3">A little about your dog</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Dog's name" testid="consult-dog-name">
                <input type="text" value={form.dog_name} onChange={(e) => update("dog_name", e.target.value)} className="form-input" />
              </Field>
              <Field label="Age (weeks or months)" testid="consult-dog-age">
                <input type="text" value={form.dog_age} onChange={(e) => update("dog_age", e.target.value)} className="form-input" placeholder="e.g. 10 weeks" />
              </Field>
            </div>
            <Field label="Anything you'd like the team to know? (optional)" testid="consult-message">
              <textarea rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} className="form-input" placeholder="e.g. We're picking up our puppy next week and would love some help getting started." />
            </Field>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="consult-submit"
          >
            {busy ? "Sending…" : "Request my free consult"} <ArrowRight size={18} weight="bold" />
          </button>
          <p className="text-xs text-stone-500 text-center">
            the team usually replies within a few hours — definitely the same business day. No spam, ever.
          </p>
        </form>

        <div className="text-center text-sm text-stone-600 mt-8">
          Prefer to chat directly? <a href="tel:0435740077" className="font-semibold text-[#f38838] hover:underline inline-flex items-center gap-1"><Phone size={14} weight="duotone" /> 0435 740 077</a>{" "}or <a href="mailto:support@puppl.app" className="font-semibold text-[#f38838] hover:underline inline-flex items-center gap-1"><EnvelopeSimple size={14} weight="duotone" /> support@puppl.app</a>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider font-semibold text-stone-500 mb-1">{label}</span>
      <div data-testid={testid}>{children}</div>
    </label>
  );
}
